export const ADMIN_DRAFT_APPROVAL_DIAGNOSTICS_KEY =
  "eye-academy:admin-draft-approval-diagnostics:v1";
export const ADMIN_DRAFT_APPROVAL_ACTIVE_KEY =
  "eye-academy:admin-draft-approval-active:v1";

const MAX_DIAGNOSTIC_ATTEMPTS = 25;
let diagnosticSender = null;

const getStorage = () => {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const parseAttempts = (storage) => {
  if (!storage) return [];

  try {
    const parsed = JSON.parse(
      storage.getItem(ADMIN_DRAFT_APPROVAL_DIAGNOSTICS_KEY) || "[]",
    );
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const createAttemptId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `approval-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeError = (error) => {
  if (!error) return null;
  return {
    type: String(error.name || "Error"),
    message: String(error.message || error),
    code: error.code ? String(error.code) : "",
  };
};

const emitDiagnostic = (event) => {
  console.info("[admin-draft-approval]", event);
};

const persistDiagnostic = (attempt) => {
  if (typeof diagnosticSender !== "function" || !attempt) return;
  Promise.resolve(diagnosticSender({ attempt })).catch((error) => {
    console.debug("[admin-draft-approval] persistence failed", {
      attemptId: attempt.attemptId,
      error: normalizeError(error),
    });
  });
};

export const configureAdminDraftApprovalDiagnosticSender = (sender) => {
  diagnosticSender = typeof sender === "function" ? sender : null;
};

export const readAdminDraftApprovalDiagnostics = (storage = getStorage()) =>
  parseAttempts(storage);

export const getActiveAdminDraftApprovalAttemptId = (
  storage = getStorage(),
) => {
  if (!storage) return "";
  try {
    return storage.getItem(ADMIN_DRAFT_APPROVAL_ACTIVE_KEY) || "";
  } catch {
    return "";
  }
};

export const startAdminDraftApprovalDiagnostic = (
  context = {},
  storage = getStorage(),
) => {
  const attemptId = createAttemptId();
  const startedAt = new Date().toISOString();
  const attempt = {
    attemptId,
    startedAt,
    updatedAt: startedAt,
    context,
    stages: [],
  };

  if (storage) {
    const attempts = parseAttempts(storage);
    attempts.push(attempt);
    storage.setItem(
      ADMIN_DRAFT_APPROVAL_DIAGNOSTICS_KEY,
      JSON.stringify(attempts.slice(-MAX_DIAGNOSTIC_ATTEMPTS)),
    );
    storage.setItem(ADMIN_DRAFT_APPROVAL_ACTIVE_KEY, attemptId);
  }

  persistDiagnostic(attempt);
  emitDiagnostic({ type: "attempt_started", ...attempt });
  return attemptId;
};

export const recordAdminDraftApprovalDiagnostic = (
  stage,
  details = {},
  { attemptId = "", error = null, storage = getStorage() } = {},
) => {
  const resolvedAttemptId =
    attemptId || getActiveAdminDraftApprovalAttemptId(storage);
  const event = {
    attemptId: resolvedAttemptId,
    stage: String(stage || "unknown"),
    timestamp: new Date().toISOString(),
    details,
    error: normalizeError(error),
  };

  if (storage && resolvedAttemptId) {
    const attempts = parseAttempts(storage);
    const index = attempts.findIndex(
      (attempt) => attempt.attemptId === resolvedAttemptId,
    );
    if (index >= 0) {
      attempts[index] = {
        ...attempts[index],
        updatedAt: event.timestamp,
        stages: [...(attempts[index].stages || []), event],
      };
      storage.setItem(
        ADMIN_DRAFT_APPROVAL_DIAGNOSTICS_KEY,
        JSON.stringify(attempts.slice(-MAX_DIAGNOSTIC_ATTEMPTS)),
      );
      persistDiagnostic(attempts[index]);
    }
  }

  emitDiagnostic({ type: "attempt_stage", ...event });
  return event;
};
